/* Yard of Cards: enhancement of Horizon, not a second cart implementation. */
import { StandardEvents } from '@shopify/events';
import { sectionRenderer } from '@theme/section-renderer';
const cfg = window.Yoc || { strings: {}, root: '/', cart: '/cart' };
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const behavior = () => reduceMotion.matches ? 'instant' : 'smooth';
let toastTimer;
function toast(message) {
  const node = document.querySelector('.yoc-toast');
  if (!node) return;
  node.textContent = message; node.hidden = false;
  clearTimeout(toastTimer); toastTimer = setTimeout(() => { node.hidden = true; }, 6500);
}
function preferencesAllowed() {
  const privacy = window.Shopify?.customerPrivacy;
  return privacy?.currentVisitorConsent?.().preferences === 'yes' && privacy?.preferencesProcessingAllowed?.() === true;
}
function mediaAllowed() {
  const privacy = window.Shopify?.customerPrivacy;
  return privacy?.currentVisitorConsent?.().marketing === 'yes' && privacy?.marketingAllowed?.() === true;
}
async function cookiePreferences() {
  try {
    if (window.privacyBanner?.showPreferences) { await window.privacyBanner.showPreferences(); return; }
    if (cfg.cookieUrl) { window.location.assign(cfg.cookieUrl); return; }
    toast(cfg.strings.consent);
  } catch { toast(cfg.strings.consent); }
}
// Shopify/CMP owns consent collection. No automatic consent is set by this theme.
if (window.Shopify?.loadFeatures && !window.Shopify.customerPrivacy) {
  window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], error => { if (!error) { loadWish(); document.dispatchEvent(new CustomEvent('yoc:privacy-ready')); } });
}
class YocCarousel extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController(); const opts = { signal: this.controller.signal };
    this.rail = this.querySelector('[data-slides]'); if (!this.rail) return;
    this.paused = reduceMotion.matches || this.dataset.autoplay !== 'true';
    this.querySelector('[data-carousel-prev]')?.addEventListener('click', () => this.step(-1), opts);
    this.querySelector('[data-carousel-next]')?.addEventListener('click', () => this.step(1), opts);
    this.querySelector('[data-carousel-pause]')?.addEventListener('click', () => { this.paused = !this.paused; this.timer(); }, opts);
    this.querySelector('[data-carousel-close]')?.addEventListener('click', () => { this.hidden = true; clearInterval(this.interval); }, opts);
    this.rail.tabIndex = 0;
    this.rail.addEventListener('keydown', e => { if (e.target !== this.rail) return; if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') { e.preventDefault(); this.step(e.key === 'ArrowRight' ? 1 : -1); } }, opts);
    this.addEventListener('focusin', () => { this.paused = true; this.timer(); }, opts);
    this.addEventListener('pointerdown', e => { if (!e.target.closest('[data-carousel-pause]')) { this.paused = true; this.timer(); } }, opts);
    reduceMotion.addEventListener('change', () => { if (reduceMotion.matches) { this.paused = true; this.timer(); } }, opts);
    this.timer();
  }
  step(direction) {
    const child = this.rail.children[0]; if (!child) return;
    const width = child.getBoundingClientRect().width + parseFloat(getComputedStyle(this.rail).gap || '0');
    const max = this.rail.scrollWidth - this.rail.clientWidth;
    let next = this.rail.scrollLeft + width * direction;
    if (direction > 0 && this.rail.scrollLeft >= max - 4) next = 0;
    if (direction < 0 && this.rail.scrollLeft <= 0) next = max;
    this.rail.scrollTo({ left: next, behavior: behavior() });
  }
  timer() {
    clearInterval(this.interval);
    const button = this.querySelector('[data-carousel-pause]');
    if (button) { button.textContent = this.paused ? '▷' : 'Ⅱ'; button.setAttribute('aria-label', this.paused ? button.dataset.play : button.dataset.pause); button.setAttribute('aria-pressed', String(this.paused)); }
    if (!this.paused && this.rail.children.length > 1) this.interval = setInterval(() => { if (!document.hidden) this.step(1); }, Number(this.dataset.interval) || 8000);
  }
  disconnectedCallback() { clearInterval(this.interval); this.controller?.abort(); }
}
class YocExpand extends HTMLElement {
  connectedCallback() {
    const content = this.querySelector('[data-expand-content]'), toggle = this.querySelector('[data-expand-toggle]');
    if (!content || !toggle) return;
    this.controller = new AbortController();
    requestAnimationFrame(() => {
      if (content.scrollHeight < (this.dataset.lines ? 62 : 265)) { toggle.hidden = true; return; }
      this.classList.add('is-collapsed');
      toggle.addEventListener('click', () => { const expanded = this.classList.toggle('is-expanded'); this.classList.toggle('is-collapsed', !expanded); toggle.setAttribute('aria-expanded', String(expanded)); toggle.textContent = expanded ? toggle.dataset.less : toggle.dataset.more; }, { signal: this.controller.signal });
    });
  }
  disconnectedCallback() { this.controller?.abort(); }
}
class YocCountdown extends HTMLElement {
  connectedCallback() {
    this.until = Date.parse(this.dataset.until); if (!Number.isFinite(this.until)) { this.hidden = true; return; }
    this.setAttribute('aria-live', 'off'); this.update(); this.interval = setInterval(() => this.update(), 60000);
  }
  update() {
    const left = this.until - Date.now();
    this.textContent = left <= 0 ? cfg.strings.expired : `${Math.ceil(left / 86400000)} ${cfg.strings.days} · ${new Intl.DateTimeFormat(document.documentElement.lang === 'en' ? 'en-GB' : 'de-DE', {timeZone:'Europe/Berlin',day:'2-digit',month:'2-digit',year:'numeric',hour:'2-digit',minute:'2-digit'}).format(this.until)}`;
    if (left <= 0) clearInterval(this.interval);
  }
  disconnectedCallback() { clearInterval(this.interval); }
}
class YocVariantFragment extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController(); this.generation = 0;
    document.addEventListener(StandardEvents.productSelect, async e => {
      const generation = ++this.generation;
      try {
        const resolved = await e.promise; if (generation !== this.generation || !this.isConnected) return;
        const detail = resolved?.detail; if (!detail?.html || (detail.productId && String(detail.productId) !== this.dataset.productId && !detail.newProduct)) return;
        const fresh = [...detail.html.querySelectorAll('yoc-variant-fragment')].find(node => node.dataset.key === this.dataset.key);
        if (fresh) { this.innerHTML = fresh.innerHTML; this.dataset.productId = fresh.dataset.productId; syncWish(); }
      } catch { /* Horizon displays the corresponding variant request error. */ }
    }, { signal: this.controller.signal });
  }
  disconnectedCallback() { this.controller?.abort(); }
}
class YocStickyBuy extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController(); const main = document.getElementById('YocBuyButtons'), bar = this.querySelector('.yoc-sticky-inner');
    if (!main || !bar || !window.IntersectionObserver) return;
    this.observer = new IntersectionObserver(entries => { const entry = entries[0]; bar.hidden = entry.isIntersecting || entry.boundingClientRect.top > 0; document.body.classList.toggle('yoc-has-sticky-buy', !bar.hidden); }, { rootMargin: '-90px 0px 0px 0px' });
    this.observer.observe(main);
    this.addEventListener('click', e => {
      if (!e.target.closest('[data-yoc-sticky-submit]')) return;
      const form = main.querySelector('form'), button = form?.querySelector('[name="add"]');
      if (!form || !button || button.disabled) { main.scrollIntoView({ behavior: behavior(), block: 'center' }); return; }
      if (form.reportValidity()) form.requestSubmit(button);
    }, { signal: this.controller.signal });
  }
  disconnectedCallback() { this.controller?.abort(); this.observer?.disconnect(); document.body.classList.remove('yoc-has-sticky-buy'); }
}
class YocShippingProgress extends HTMLElement {
  refresh(cart) { this.dataset.total=String(cart.total_price); if (cart.currency) this.dataset.currency=cart.currency; this.hidden=true; this.connectedCallback(); }
  connectedCallback() {
    if (this.dataset.currency !== this.dataset.shopCurrency) return;
    const threshold = Number(this.dataset.threshold), total = Number(this.dataset.total);
    if (!Number.isFinite(threshold) || threshold <= 0 || !Number.isFinite(total)) return;
    const remaining = Math.max(0, threshold - total);
    try {
      const money = new Intl.NumberFormat(document.documentElement.lang, {style:'currency', currency:this.dataset.currency}).format(remaining / 100);
      this.querySelector('[data-shipping-label]').textContent = remaining > 0 ? this.dataset.remaining.replace('[amount]', money) : this.dataset.reached;
      this.querySelector('progress').value = Math.min(100, total / threshold * 100); this.hidden = false;
    } catch { this.hidden = true; }
  }
}
class YocEmbed extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    this.querySelector('[data-embed-load]')?.addEventListener('click', () => {
      if (!mediaAllowed()) { cookiePreferences(); return; }
      const id = this.dataset.videoId; let url;
      if (this.dataset.host === 'youtube' && /^[\w-]{6,32}$/.test(id)) url = `https://www.youtube-nocookie.com/embed/${encodeURIComponent(id)}?autoplay=1`;
      else if (this.dataset.host === 'vimeo' && /^\d+$/.test(id)) url = `https://player.vimeo.com/video/${id}?dnt=1&autoplay=1`;
      else return;
      const frame = document.createElement('iframe'); frame.src = url; frame.title = this.dataset.title || cfg.strings.mediaTitle || 'Video'; frame.allow = 'autoplay; fullscreen'; frame.allowFullscreen = true;
      this.querySelector('[data-embed-placeholder]').hidden = true; this.append(frame);
    }, { signal: this.controller.signal });
    document.addEventListener('visitorConsentCollected', () => { if (!mediaAllowed()) { this.querySelector('iframe')?.remove(); const p = this.querySelector('[data-embed-placeholder]'); if (p) p.hidden = false; } }, { signal: this.controller.signal });
  }
  disconnectedCallback() { this.controller?.abort(); }
}
class YocWithdrawal extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController(); const opts = { signal: this.controller.signal };
    const form = this.querySelector('form'), review = this.querySelector('[data-withdraw-review]'), summary = this.querySelector('[data-withdraw-summary]');
    if (!form || !review || !summary) return;
    review.querySelector('summary').addEventListener('click', e => {
      if (!form.reportValidity()) { e.preventDefault(); return; }
      summary.replaceChildren();
      for (const input of form.querySelectorAll('input:not([type=hidden]), textarea')) {
        const dt = document.createElement('dt'), dd = document.createElement('dd'); dt.textContent = input.closest('label')?.childNodes[0]?.textContent || input.name; dd.textContent = input.value; summary.append(dt, dd);
      }
    }, opts);
    this.querySelector('[data-withdraw-back]')?.addEventListener('click', () => { review.open = false; form.querySelector('input:not([type=hidden])')?.focus(); }, opts);
    form.addEventListener('submit', e => {
      if (!review.open) { e.preventDefault(); review.querySelector('summary').click(); return; }
      form.querySelector('[data-withdraw-time]').value = new Date().toISOString();
    }, opts);
  }
  disconnectedCallback() { this.controller?.abort(); }
}
// Consent-aware wishlist: current product sections, bounded local list, explicit sharing and bulk purchase.
let wishlist = new Set(); const wishKey = 'yoc-wishlist-v1';
function loadWish() {
  if (preferencesAllowed()) { try { const data = JSON.parse(localStorage.getItem(wishKey) || '[]'); if (Array.isArray(data)) wishlist = new Set(data.filter(x => typeof x === 'string' && /^[a-z0-9-]+$/.test(x)).slice(0,100)); } catch { /* Keep in-memory list. */ } }
  syncWish(); document.dispatchEvent(new CustomEvent('yoc:wishlist'));
}
function syncWish() {
  document.querySelectorAll('[data-yoc-wish]').forEach(button => { const saved = wishlist.has(button.dataset.yocWish); button.setAttribute('aria-pressed', String(saved)); button.title = saved ? cfg.strings.wishRemove : cfg.strings.wishAdd; });
  document.querySelectorAll('[data-yoc-wish-count]').forEach(node => { node.textContent = String(wishlist.size); });
}
class YocWishlist extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController(); const {signal}=this.controller;
    this.readShared(); window.addEventListener('hashchange',()=>{ this.readShared(); this.render(); },{signal});
    document.addEventListener('yoc:wishlist',()=>{ if (!this.bulkPending) this.render(); },{signal});
    this.addEventListener('click',e=>{
      if (e.target.closest('[data-wish-bulk]')) this.bulk();
      if (e.target.closest('[data-wish-share]')) this.share();
      if (e.target.closest('[data-wish-import]')) {
        if (this.bulkPending) return;
        for (const handle of this.shared || []) { if (wishlist.size >= 100) break; wishlist.add(handle); }
        this.persist(); syncWish(); document.dispatchEvent(new CustomEvent('yoc:wishlist'));
        toast(preferencesAllowed() ? cfg.strings.wishSaved : cfg.strings.wishSession);
      }
    },{signal});
    this.render();
  }
  readShared() {
    const hash=location.hash.slice(1), params=new URLSearchParams(hash);
    this.shared=params.has('wish') ? [...new Set((hash.length <= 26000 ? params.get('wish') : '').split(',').filter(x=>/^[a-z0-9-]{1,255}$/.test(x)))].slice(0,100) : null;
    this.querySelector('[data-wish-shared]').hidden=this.shared === null;
  }
  persist() { if (preferencesAllowed()) { try { localStorage.setItem(wishKey,JSON.stringify([...wishlist].slice(0,100))); } catch { /* Memory is still usable. */ } } }
  handles() { return this.shared === null ? [...wishlist].slice(0,100) : this.shared; }
  async currentProducts(handles, signal) {
    const results=new Array(handles.length); let index=0;
    await Promise.all(Array.from({length:Math.min(4,handles.length)},async()=>{
      while (index < handles.length) {
        const i=index++, handle=handles[i]; if (signal.aborted) return;
        try {
          const url=`${cfg.root.replace(/\/$/,'')}/products/${encodeURIComponent(handle)}?section_id=yoc-wishlist-card`;
          const response=await fetch(url,{signal,cache:'no-store'});
          if (response.status===404) { results[i]={missing:true}; continue; }
          if (!response.ok) throw new Error('Request failed');
          const doc=new DOMParser().parseFromString(await response.text(),'text/html'), card=doc.querySelector('[data-wish-product]');
          results[i]=card?.dataset.handle===handle ? {card} : {missing:true};
        } catch (error) { if (error.name==='AbortError') return; results[i]={error:true}; }
      }
    }));
    return results;
  }
  setBusy(value) { if (value) this.setAttribute('aria-busy','true'); else this.removeAttribute('aria-busy'); this.querySelectorAll('[data-wish-bulk],[data-wish-share],[data-wish-import]').forEach(button=>{ button.disabled=value || !this.handles().length; }); }
  async render() {
    this.fetchController?.abort(); this.fetchController=new AbortController(); const {signal}=this.fetchController;
    const list=this.querySelector('[data-wish-items]'), status=this.querySelector('[data-wish-status]'); if (!list) return;
    const handles=this.handles(); this.querySelector('[data-wish-empty]').hidden=handles.length>0; this.setBusy(true);
    status.textContent=handles.length ? cfg.strings.wishLoading : '';
    const results=await this.currentProducts(handles,signal); if (signal.aborted || !this.isConnected) return;
    list.replaceChildren(...results.filter(x=>x?.card).map(x=>x.card)); syncWish();
    status.textContent=results.some(x=>!x?.card) ? cfg.strings.wishMissing : '';
    this.setBusy(false);
  }
  async bulk() {
    if (this.bulkPending || this.hasAttribute('aria-busy')) return;
    this.bulkPending=true; this.setBusy(true); this.fetchController?.abort(); this.fetchController=new AbortController();
    const {signal}=this.fetchController, status=this.querySelector('[data-wish-status]'); status.textContent=cfg.strings.wishLoading;
    try {
      const products=await this.currentProducts(this.handles(),signal);
      if (signal.aborted) return;
      if (products.some(item=>item?.error)) throw new Error(cfg.strings.network);
      const ids=[...new Set(products.map(item=>item?.card?.dataset.variantId).filter(id=>/^\d+$/.test(id || '')))];
      if (!ids.length) { status.textContent=cfg.strings.wishNoneBuyable; return; }
      const sections=[...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map(el=>el.dataset.sectionId))].slice(0,5);
      const response=await fetch(`${cfg.cart}/add.js`,{method:'POST',signal,headers:{'Content-Type':'application/json',Accept:'application/json'},body:JSON.stringify({items:ids.map(id=>({id,quantity:1})),sections,sections_url:location.pathname})});
      const data=await response.json();
      if (!response.ok || data.status >= 400) throw new Error(typeof data.description === 'string' ? data.description : cfg.strings.wishBulkFailed);
      window.location.assign(cfg.cart);
    } catch (error) { if (error.name!=='AbortError') status.textContent=error.message === cfg.strings.network ? cfg.strings.network : `${cfg.strings.wishBulkFailed} ${error.message && error.message !== 'Failed to fetch' ? error.message : ''}`; }
    finally { this.bulkPending=false; this.setBusy(false); }
  }
  async share() {
    const url=new URL(location.href); url.hash=`wish=${this.handles().join(',')}`; url.search='';
    try { if (navigator.share) await navigator.share({title:document.title,url:url.href}); else { await navigator.clipboard.writeText(url.href); toast(cfg.strings.copied); } }
    catch (error) { if (error.name==='AbortError') return; const label=this.querySelector('[data-wish-copy]'), input=label.querySelector('input'); label.hidden=false; input.value=url.href; input.focus(); input.select(); }
  }
  disconnectedCallback() { this.controller?.abort(); this.fetchController?.abort(); }
}
const components = {'yoc-carousel':YocCarousel,'yoc-expand':YocExpand,'yoc-countdown':YocCountdown,'yoc-variant-fragment':YocVariantFragment,'yoc-sticky-buy':YocStickyBuy,'yoc-shipping-progress':YocShippingProgress,'yoc-embed':YocEmbed,'yoc-withdrawal':YocWithdrawal,'yoc-wishlist':YocWishlist};
for (const [name,component] of Object.entries(components)) if (!customElements.get(name)) customElements.define(name, component);
document.documentElement.classList.toggle('yoc-holo', cfg.holo === true);
document.addEventListener('click', async e => {
  const target = e.target instanceof Element ? e.target : null; if (!target) return;
  if (target.closest('[data-yoc-cookie-preferences]')) { e.preventDefault(); cookiePreferences(); }
  const wish = target.closest('[data-yoc-wish]');
  if (wish) {
    const handle = wish.dataset.yocWish; if (!/^[a-z0-9-]+$/.test(handle || '')) return;
    if (wish.closest('yoc-wishlist')?.bulkPending) return;
    if (!wishlist.has(handle) && wishlist.size >= 100) { toast(cfg.strings.wishFull); return; }
    wishlist.has(handle) ? wishlist.delete(handle) : wishlist.add(handle);
    if (preferencesAllowed()) { try { localStorage.setItem(wishKey,JSON.stringify([...wishlist].slice(0,100))); } catch { /* in-memory remains usable */ } }
    syncWish(); document.dispatchEvent(new CustomEvent('yoc:wishlist')); toast(preferencesAllowed() ? cfg.strings.wishSaved : cfg.strings.wishSession);
  }
  const share = target.closest('[data-yoc-share]');
  if (share) { try { if (navigator.share) await navigator.share({url:share.dataset.yocShare,title:document.title}); else { await navigator.clipboard.writeText(share.dataset.yocShare); toast(cfg.strings.copied); } } catch (error) { if (error.name !== 'AbortError') toast(cfg.strings.network); } }
  const filter = target.closest('[data-yoc-filter-url]');
  if (filter && !e.ctrlKey && !e.metaKey && !e.shiftKey) { const form = filter.closest('facets-form-component'); if (form?.updateFiltersByURL) { e.preventDefault(); form.updateFiltersByURL(filter.href); } }
});
document.addEventListener('yoc:content', syncWish);
document.addEventListener('visitorConsentCollected', () => {
  if (!preferencesAllowed()) { try { localStorage.removeItem(wishKey); localStorage.removeItem('viewedProducts'); } catch { /* unavailable storage */ } }
  else { try { const saved = JSON.parse(localStorage.getItem(wishKey)||'[]'); if (Array.isArray(saved)) for (const handle of saved) if (wishlist.size < 100 && /^[a-z0-9-]{1,255}$/.test(handle)) wishlist.add(handle); localStorage.setItem(wishKey,JSON.stringify([...wishlist].slice(0,100))); } catch { /* retain in memory */ } }
  syncWish(); document.dispatchEvent(new CustomEvent('yoc:wishlist'));
});
loadWish();
let refreshingCart = false;
async function refreshCartCount() {
  if (document.hidden || refreshingCart) return; refreshingCart = true;
  try { const response = await fetch(`${cfg.cart}.js`,{headers:{Accept:'application/json'},cache:'no-store'}); if (!response.ok) return; const cart = await response.json(); document.querySelectorAll('cart-icon').forEach(icon => icon.renderCartBubble?.(cart.item_count,false)); document.querySelectorAll('yoc-shipping-progress').forEach(progress=>progress.refresh?.(cart)); document.dispatchEvent(new CustomEvent('yoc:cart-changed',{detail:cart})); }
  catch { /* Never overwrite a correct count with zero on a network error. */ }
  finally { refreshingCart = false; }
}
document.addEventListener('visibilitychange', () => { if (!document.hidden) refreshCartCount(); });
window.addEventListener('pageshow', e => { if (e.persisted) refreshCartCount(); });
document.addEventListener(StandardEvents.cartLinesUpdate, async e => {
  try { const result = await e.promise; if (result?.detail?.didError) return; await refreshCartCount(); if (e.action !== 'add') return; if (cfg.afterAdd === 'page') window.location.assign(cfg.cart); else if (cfg.afterAdd === 'toast') toast(cfg.strings.added); } catch { /* native form provides the error */ }
});
window.addEventListener('popstate', async () => {
  const results = document.querySelector('results-list'); if (!results) return;
  results.setAttribute('aria-busy','true');
  try { await sectionRenderer.renderSection(results.getAttribute('section-id')); } catch { window.location.reload(); }
  finally { document.querySelector('results-list')?.removeAttribute('aria-busy'); }
});
// A plan change uses the documented URL selection. Shopify renders the correct allocation price.
document.addEventListener('change', e => {
  const select = e.target;
  if (!(select instanceof HTMLSelectElement) || !select.matches('[data-yoc-plan]')) return;
  const form = select.closest('form'), variant = form?.querySelector('[name="id"]')?.value;
  const url = new URL(window.location.href);
  if (variant) url.searchParams.set('variant', variant);
  if (select.value) url.searchParams.set('selling_plan', select.value); else url.searchParams.delete('selling_plan');
  window.location.assign(url.href);
});
